// frontend/src/lib/api/serviceTypeService.ts
// UPDATED: Refactored API service with proper error handling and consistent naming

import { BaseApiService } from './baseService';
import { ApiResponse } from '../httpClient';

export interface ServiceType {
  service_type_id: number;
  category_id: number;
  name: string;
  description?: string;
  slug_url?: string;
  is_active: boolean;
  is_approved: boolean;
  created_by_user_id: number;
  created_at?: string;
  updated_at?: string;
  category_name?: string;
  created_by_name?: string;
  usage_count?: number;
  can_delete?: boolean;
}

export interface ServiceTypeFilters {
  page?: number;
  limit?: number;
  search?: string;
  category?: string;
  status?: string;
  approval?: string;
}

export interface ServiceTypeFormData {
  category_id: number;
  name: string;
  description?: string;
  is_active: boolean;
}

export interface ServiceTypeStats {
  total: number;
  active: number;
  pending: number;
  inactive: number;
  approved: number;
  rejected: number;
}

export interface ServiceTypePagination {
  current_page: number;
  total_pages: number;
  total_items: number;
  items_per_page: number;
  has_next: boolean;
  has_prev: boolean;
}

export interface ServiceTypeResponse {
  service_types: ServiceType[];
  pagination: ServiceTypePagination;
  filters: ServiceTypeFilters;
  stats: ServiceTypeStats;
}

export interface Category {
  category_id: number;
  name: string;
  description?: string;
  icon?: string;
  slug_url?: string;
  is_active?: boolean;
  parent_category_id?: number | null;
  display_order?: number;
  subcategory_count?: number;
  created_at?: string;
  updated_at?: string;
}

class ServiceTypeService extends BaseApiService {
  constructor() {
    super({
      baseEndpoint: '/service-types',
      timeout: 10000,
    });
  }

  async getServiceTypes(filters: ServiceTypeFilters = {}): Promise<ApiResponse<ServiceTypeResponse>> {
    try {
      return await this.get('', { params: filters });
    } catch (error: any) {
      throw new Error(error.message || 'Failed to fetch service types');
    }
  }

  async getServiceTypeById(id: number): Promise<ApiResponse<{ service_type: ServiceType }>> {
    try {
      return await this.get(`/${id}`);
    } catch (error: any) {
      throw new Error(error.message || 'Failed to fetch service type');
    }
  }

  async createServiceType(data: ServiceTypeFormData): Promise<ApiResponse<{ service_type: ServiceType }>> {
    try {
      return await this.post('', data);
    } catch (error: any) {
      throw new Error(error.message || 'Failed to create service type');
    }
  }

  async updateServiceType(id: number, data: ServiceTypeFormData): Promise<ApiResponse<{ service_type: ServiceType }>> {
    try {
      return await this.put(`/${id}`, data);
    } catch (error: any) {
      throw new Error(error.message || 'Failed to update service type');
    }
  }

  async deleteServiceType(id: number): Promise<ApiResponse<void>> {
    try {
      return await this.delete(`/${id}`);
    } catch (error: any) {
      throw new Error(error.message || 'Failed to delete service type');
    }
  }

  async toggleServiceTypeStatus(id: number): Promise<ApiResponse<{ service_type: ServiceType }>> {
    try {
      return await this.post(`/${id}/toggle-status`);
    } catch (error: any) {
      throw new Error(error.message || 'Failed to toggle service type status');
    }
  }

  async approveServiceType(id: number): Promise<ApiResponse<{ service_type: ServiceType }>> {
    try {
      return await this.post(`/${id}/approve`);
    } catch (error: any) {
      throw new Error(error.message || 'Failed to approve service type');
    }
  }

  async rejectServiceType(id: number): Promise<ApiResponse<{ service_type: ServiceType }>> {
    try {
      return await this.post(`/${id}/reject`);
    } catch (error: any) {
      throw new Error(error.message || 'Failed to reject service type');
    }
  }

  async getPendingServiceTypes(filters: ServiceTypeFilters = {}): Promise<ApiResponse<{
    service_types: ServiceType[];
    pagination: ServiceTypePagination;
  }>> {
    try {
      return await this.get('/pending', { params: filters });
    } catch (error: any) {
      throw new Error(error.message || 'Failed to fetch pending service types');
    }
  }

  async bulkApproveServiceTypes(serviceTypeIds: number[]): Promise<ApiResponse<{
    approved: number;
    failed: number;
    errors?: string[];
  }>> {
    try {
      return await this.post('/bulk-approve', { service_type_ids: serviceTypeIds });
    } catch (error: any) {
      throw new Error(error.message || 'Failed to bulk approve service types');
    }
  }

  async bulkRejectServiceTypes(serviceTypeIds: number[]): Promise<ApiResponse<{
    rejected: number;
    failed: number;
    errors?: string[];
  }>> {
    try {
      return await this.post('/bulk-reject', { service_type_ids: serviceTypeIds });
    } catch (error: any) {
      throw new Error(error.message || 'Failed to bulk reject service types');
    }
  }

  async bulkDeleteServiceTypes(serviceTypeIds: number[]): Promise<ApiResponse<{
    deleted: number;
    failed: number;
    errors?: string[];
  }>> {
    try {
      return await this.post('/bulk-delete', { service_type_ids: serviceTypeIds });
    } catch (error: any) {
      throw new Error(error.message || 'Failed to bulk delete service types');
    }
  }

  async getServiceTypeStats(): Promise<ApiResponse<ServiceTypeStats>> {
    try {
      return await this.get('/stats');
    } catch (error: any) {
      throw new Error(error.message || 'Failed to fetch service type statistics');
    }
  }

  async exportServiceTypes(filters: ServiceTypeFilters = {}): Promise<ApiResponse<{ download_url: string }>> {
    try {
      return await this.get('/export', { params: filters });
    } catch (error: any) {
      throw new Error(error.message || 'Failed to export service types');
    }
  }
}

export const serviceTypeService = new ServiceTypeService();

// Export default instance for backward compatibility
export default serviceTypeService;